import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

import Modal from '../base/Modal';
import {
  API_URL,
  deleteOutlookMailsFromDB,
  deleteGmailMailsFromDB,
} from '../../services/api';
import { DataState } from '../../context/DataProvider';
import { useEmails } from '../../context/EmailsProvider';

const ResourceSynchronizationModal = ({
  isOpen,
  onClose,
  appName,
  onlyEmailApps,
  setJiraSynced,
  setFoundJiraSync,
  setConfirmLoading,
}) => {
  const { userGoogleId } = DataState();
  const [jiraIssuesCount, setJiraIssuesCount] = useState(0);
  const [jiraCommentsCount, setJiraCommentsCount] = useState(0);
  const [jiraUsersCount, setJiraUsersCount] = useState(0);
  const [emailsCount, setEmailsCount] = useState(0);
  const [needFetch, setNeedFetch] = useState(true);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('Pending');
  const {
    setGmailClearStatus,
    setOutlookClearStatus,
    setGmailSyncStatus,
    setOutlookSyncStatus,
    setForceRefetch,
    setIsOutlook,
  } = useEmails();

  useEffect(() => {
    // Simulate API call to get data (replace with actual API request)
    const fetchResourceData = async () => {
      try {
        let data = { synchronization_status: 'Pending' };
        if (appName === 'jira') {
          // Replace with real API request for Jira data
          const response = await fetch(`${API_URL}/auth/jira/getCounts`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${userGoogleId}` },
            credentials: 'include',
          });
          data = await response.json();
          setJiraIssuesCount(data.issues);
          setJiraCommentsCount(data.comments);
          setJiraUsersCount(data.users);
          const jiraTotal = data.issues + data.comments + data.users;
          if (jiraTotal <= 0) {
            setJiraSynced(false);
          } else if (jiraTotal > 0) {
            setJiraSynced(true);
          }
          setFoundJiraSync(true);
          setConfirmLoading(false);
        } else if (appName === 'microsoft_outlook') {
          const response = await fetch(`${API_URL}/auth/outlook/getCounts`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${userGoogleId}` },
            credentials: 'include',
          });
          data = await response.json();
          setEmailsCount(data.emails);
          // setIsSyncing(!isSyncing);
        } else if (appName === 'gmail') {
          const response = await fetch(`${API_URL}/auth/gmail/getCounts`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${userGoogleId}` },
            credentials: 'include',
          });
          data = await response.json();
          setEmailsCount(data.emails);
          // setIsSyncing(!isSyncing);
        }

        setStatus(data.synchronization_status || 'Pending');
      } catch (error) {
        toast.error('Failed to fetch resource data.');
      }
    };
    if (needFetch) {
      fetchResourceData();
      setNeedFetch(false);
    }
    // eslint-disable-next-line
  }, [needFetch]);

  const handleSynchronize = async () => {
    setStatus('Synchronizing');
    // Simulate synchronization process
    const synchronize = async () => {
      try {
        let response = null;
        if (appName === 'jira') {
          response = await fetch(`${API_URL}/jira_issues_and_comments/store`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${userGoogleId}` },
            credentials: 'include',
          });
        } else if (appName === 'microsoft_outlook') {
          response = await fetch(`${API_URL}/outlook_mails/store`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${userGoogleId}` },
            credentials: 'include',
          });
        } else if (appName === 'gmail') {
          response = await fetch(`${API_URL}/gmail_emails/store`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${userGoogleId}` },
            credentials: 'include',
          });
        }
        // Replace with real API request for Jira data

        if (!response.ok) {
          const error_response = await response.json();
          throw (
            error_response.error ||
            error_response.detail ||
            'Error occured during Synchronization'
          );
        }
        await response.json();
        setStatus('Synchronized');
        toast.success('Synchronization complete!');
        if (onlyEmailApps) {
          setForceRefetch(true);
          if (appName === 'microsoft_outlook') {
            setOutlookSyncStatus((prev) => ({
              status: true,
              state: prev.state + 1,
            }));
            setIsOutlook(true);
          } else if (appName === 'gmail') {
            setGmailSyncStatus((prev) => ({
              status: true,
              state: prev.state + 1,
            }));
            setIsOutlook(false);
          }
        }
      } catch (error) {
        setStatus('Interrupted');
        toast.error(JSON.stringify(error));
      } finally {
        setNeedFetch(true);
      }
    };
    await synchronize();
  };

  const handleClearData = async () => {
    // Logic to clear or reset data
    setLoading(true);
    const clearData = async () => {
      try {
        // Replace with real API request for Jira data
        // eslint-disable-next-line
        if (appName == 'jira') {
          const response = await fetch(
            `${API_URL}/jira_issues_and_comments/delete`,
            {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${userGoogleId}` },
              credentials: 'include',
            },
          );
          if (!response.ok) {
            const error_response = await response.json();
            throw (
              error_response.error ||
              error_response.detail ||
              'Error occured during Synchronization'
            );
          }

          setStatus('Pending');
          toast.success('Jira Data Cleared');
        } else if (appName === 'microsoft_outlook') {
          await deleteOutlookMailsFromDB(userGoogleId);

          if (onlyEmailApps) {
            setOutlookClearStatus((prev) => ({
              status: true,
              state: prev.state + 1,
            }));
          }
        } else if (appName === 'gmail') {
          await deleteGmailMailsFromDB(userGoogleId);

          if (onlyEmailApps) {
            setGmailClearStatus((prev) => ({
              status: true,
              state: prev.state + 1,
            }));
          }
        }
      } catch (error) {
        toast.error(JSON.stringify(error));
      } finally {
        setLoading(false);
        setNeedFetch(true);
      }
    };
    await clearData();
  };

  return (
    <Modal
      title={
        `Synchronize ${appName === 'jira' ? 'Jira' : appName === 'gmail' ? 'Gmail' : 'Outlook'}` +
        (status ? ` - ${status}` : '')
      }
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="space-y-6 p-6">
        {/* Display the Jira counts */}
        <div className="space-y-2 text-xs text-gray-300">
          {appName === 'jira' && (
            <>
              <div className="flex justify-between">
                <span className="font-semibold">Jira Issues Count:</span>
                <span className="text-gray-200">{jiraIssuesCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Jira Comments Count:</span>
                <span className="text-gray-200">{jiraCommentsCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Jira Users Count:</span>
                <span className="text-gray-200">{jiraUsersCount}</span>
              </div>
            </>
          )}
          {(appName === 'microsoft_outlook' || appName === 'gmail') && (
            <>
              <div className="flex justify-between">
                <span className="font-semibold">
                  {appName === 'gmail' ? 'Gmail' : 'Outlook'} Emails Count:
                </span>
                <span className="text-gray-200">{emailsCount}</span>
              </div>
            </>
          )}
        </div>

        {/* Synchronize button */}
        <div className="mt-4 flex justify-end gap-4">
          <button
            // disabled = {(loading || status === "Synchronizing" || (jiraIssuesCount + jiraCommentsCount + jiraUsersCount) === 0)}
            disabled={
              loading ||
              status === 'Synchronizing' ||
              jiraIssuesCount +
                jiraCommentsCount +
                jiraUsersCount +
                emailsCount ===
                0
            }
            onClick={handleClearData}
            // className={"bg-red-600 text-white hover:bg-red-700 py-2 px-6 rounded-md transition-all duration-300"}
            className={
              loading || status === 'Synchronizing'
                ? 'cursor-not-allowed rounded-md bg-gray-600 px-6 py-2 text-gray-400'
                : 'rounded-md bg-red-600 px-6 py-2 text-white transition-all duration-300 hover:bg-red-700'
            }
          >
            Clear All Data
          </button>
          {/* Synchronize Button */}
          <button
            onClick={handleSynchronize}
            // disabled={status === "Synchronizing" || status === "Synchronized"}
            className={`${
              status === 'Synchronizing'
                ? 'cursor-not-allowed bg-gray-600 text-gray-400'
                : status === 'Synchronized'
                  ? 'cursor-not-allowed bg-green-600 text-white'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
            } rounded-md px-6 py-2 transition-all duration-300 disabled:opacity-50`}
          >
            {status === 'Synchronizing'
              ? 'Synchronizing...'
              : status === 'Synchronized'
                ? 'Synchronized'
                : 'Synchronize'}
          </button>

          {/* Clear All Data Button */}
        </div>
      </div>
    </Modal>
  );
};

export default ResourceSynchronizationModal;
